import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, UsageSchoolDetail } from '../api';
import { Layout, Loading, ErrorBox } from '../components/Layout';
import { STATUS_STYLE, expiryStyle, expiryLabel, relativeDay, WINDOWS } from './usage-shared';

const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Super admin',
  SCHOOL_ADMIN: 'School admin',
  COORDINATOR: 'Coordinator',
  TEACHER: 'Teacher',
};

/**
 * Phase 7 Part B — read-only drill-in for one school.
 *
 * Deliberately has no edit affordances: the super admin already has a full
 * management console at /admin/schools/:id, and mixing the two would make it
 * easy to change a licence while trying to read one. This view answers "what
 * shape is this school in", and links across for anything that changes it.
 */
export function AdminSchoolUsage() {
  const { id } = useParams<{ id: string }>();
  const [days, setDays] = useState<number | undefined>(30);
  const [data, setData] = useState<UsageSchoolDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let live = true;
    setData(null);
    setError(null);

    void api.admin.usage
      .school(id, days)
      .then((d) => live && setData(d))
      .catch((e: Error) => live && setError(e.message));

    return () => {
      live = false;
    };
  }, [id, days]);

  return (
    <Layout>
      <div className="mb-4">
        <Link to="/admin/usage" className="text-sm text-slate-500 hover:text-slate-900">
          ← Usage
        </Link>
      </div>

      {error && <ErrorBox message={error} />}
      {!data && !error && <Loading />}

      {data && (
        <>
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{data.school.name}</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {[data.school.contactName, data.school.email, data.school.phone]
                  .filter(Boolean)
                  .join(' · ') || 'No contact details'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-slate-600">
                Activity{' '}
                <select
                  value={days ?? 'all'}
                  onChange={(e) =>
                    setDays(e.target.value === 'all' ? undefined : Number(e.target.value))
                  }
                  className="ml-1 border border-slate-300 rounded-md px-2 py-1 text-sm bg-white"
                >
                  {WINDOWS.map((w) => (
                    <option key={w.label} value={w.days ?? 'all'}>
                      {w.label}
                    </option>
                  ))}
                </select>
              </label>
              <Link
                to={`/admin/schools/${data.school.id}`}
                className="text-sm px-3 py-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Edit school
              </Link>
            </div>
          </div>

          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <Card label="Status">
              <span
                className={
                  'inline-block px-2 py-0.5 rounded border text-xs ' +
                  (STATUS_STYLE[data.school.status] ?? '')
                }
              >
                {data.school.status}
              </span>
            </Card>
            <Card label="Seats">
              <span className="text-lg font-semibold tabular-nums text-slate-900">
                {data.seatsUsed}
                {data.school.seatLimit != null ? ` / ${data.school.seatLimit}` : ' / ∞'}
              </span>
            </Card>
            <Card label="Licence ends">
              <span className={'text-sm ' + expiryStyle(data.school.daysToExpiry)}>
                {expiryLabel(data.school.licenseEnd, data.school.daysToExpiry)}
              </span>
            </Card>
            <Card label="Last activity">
              <span className="text-sm text-slate-700">{relativeDay(data.lastActivityAt)}</span>
            </Card>
          </section>

          <h2 className="text-sm font-semibold text-slate-700 mb-2">
            Entitlements{' '}
            <span className="font-normal text-slate-500">({data.entitlements.length})</span>
          </h2>
          <div className="border border-slate-200 rounded-md bg-white p-3 mb-8">
            {data.entitlements.length === 0 ? (
              <p className="text-amber-700 text-sm">
                None — this school's users see an empty catalog.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {data.entitlements.map((e) => (
                  <span
                    key={e.id}
                    className="inline-block px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-xs text-slate-700"
                  >
                    {e.gradeName}
                    {/* No subject name is the whole-grade grant, not a missing
                        value — same convention as the entitlements editor. */}
                    {e.subjectName ? ` · ${e.subjectName}` : ' · whole grade'}
                  </span>
                ))}
              </div>
            )}
          </div>

          <h2 className="text-sm font-semibold text-slate-700 mb-2">
            Users <span className="font-normal text-slate-500">({data.users.length})</span>
          </h2>
          <div className="overflow-x-auto border border-slate-200 rounded-md bg-white mb-8">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Email</th>
                  <th className="text-left font-medium px-3 py-2">Role</th>
                  <th className="text-left font-medium px-3 py-2">Status</th>
                  <th className="text-right font-medium px-3 py-2">Scopes</th>
                  <th className="text-left font-medium px-3 py-2">Last login</th>
                  <th className="text-left font-medium px-3 py-2">Created by</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-900">{u.email}</td>
                    <td className="px-3 py-2 text-slate-700">{ROLE_LABEL[u.role] ?? u.role}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          u.status === 'ACTIVE' ? 'text-slate-600' : 'text-amber-700'
                        }
                      >
                        {u.status === 'ACTIVE' ? 'Active' : 'Suspended'}
                      </span>
                      {u.locked && (
                        <span className="ml-1 text-xs text-red-700">(locked out)</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      {u.scopeCount}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{relativeDay(u.lastLoginAt)}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {u.createdBy?.email ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.users.length === 0 && (
              <p className="text-slate-500 px-3 py-4">No users yet.</p>
            )}
          </div>

          <h2 className="text-sm font-semibold text-slate-700 mb-2">
            Activity{' '}
            <span className="font-normal text-slate-500">
              — {data.activity.views} viewed, {data.activity.downloads} downloaded
            </span>
          </h2>
          {data.activity.top.length === 0 ? (
            <p className="text-slate-500 border border-slate-200 rounded-md bg-white px-3 py-4">
              No recorded activity in this window.
            </p>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-md bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Lesson</th>
                    <th className="text-left font-medium px-3 py-2">Grade / subject</th>
                    <th className="text-left font-medium px-3 py-2">Variant</th>
                    <th className="text-right font-medium px-3 py-2">Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {data.activity.top.map((t) => (
                    <tr key={t.videoFileId} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <span className="text-slate-900">{t.videoTitle}</span>
                        {t.bookTitle && (
                          <span className="block text-xs text-slate-500">{t.bookTitle}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {[t.gradeName, t.subjectName].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-500 text-xs">
                        {[t.language, t.quality].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {t.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white px-3 py-2">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
